import React from "react";
import { ArrowLeft } from "lucide-react";
import { LogoLockup } from "@/components/Logo";

/**
 * Boilerplate Privacy Policy.
 * Drafted as a starting point for carrier review. NOT a substitute for
 * counsel; replace specific clauses with your final reviewed copy before
 * relying on this in production.
 */

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24 mb-10">
    <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-slate-50 mb-4">
      {title}
    </h2>
    <div className="text-slate-300 leading-relaxed space-y-4 text-[15px] md:text-base">
      {children}
    </div>
  </section>
);

const Privacy = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800/60">
        <div className="max-w-4xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <span className="hidden sm:block">
              <LogoLockup size={36} />
            </span>
            <span className="sm:hidden">
              <LogoLockup size={28} />
            </span>
          </a>
          <a
            href="/"
            className="inline-flex items-center gap-2 text-slate-300 hover:text-cyan-300 transition-colors text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="mb-14 max-w-3xl">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-400 mb-4">
            Legal
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight leading-[0.98] mb-5 text-balance">
            Privacy Policy
          </h1>
          <p className="text-slate-400 text-base md:text-lg leading-relaxed">
            This Privacy Policy describes how Cleveland Creative Group
            ("Company," "we," "us," or "our") collects, uses, and shares
            information about you when you visit our website, complete our
            forms, or otherwise interact with our services.
          </p>
          <p className="text-slate-500 text-sm mt-4">
            Last updated:{" "}
            {new Date().toLocaleDateString(undefined, {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </p>
        </div>

        <Section id="info-we-collect" title="Information We Collect">
          <p>
            We collect information you provide directly to us, such as when you
            complete a contact form, submit a payment request, or enroll in our
            text messaging program. This may include your name, email address,
            mobile phone number, mailing address, and any details you choose to
            share regarding your account or inquiry.
          </p>
          <p>
            We also automatically collect certain technical information when
            you interact with our website, including IP address, browser type,
            device identifiers, pages viewed, and timestamps. This information
            helps us operate the site, prevent abuse, and improve user
            experience.
          </p>
        </Section>

        <Section id="how-we-use" title="How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Provide, maintain, and improve our services</li>
            <li>Respond to your inquiries and service requests</li>
            <li>
              Send text messages and other communications you have consented to
              receive
            </li>
            <li>Comply with legal obligations and enforce our agreements</li>
            <li>Detect, prevent, and address fraud or security issues</li>
          </ul>
        </Section>

        <Section id="sms-info" title="SMS Information">
          <p>
            <strong className="text-slate-100">
              Mobile information will not be shared with third parties or
              affiliates for marketing or promotional purposes.
            </strong>{" "}
            Information shared with subprocessors solely to deliver text
            messages on our behalf (such as messaging service providers) is
            not considered sharing for marketing purposes. All categories of
            data above are subject to this restriction.
          </p>
          <p>
            For full details about our text messaging program, please review
            our{" "}
            <a
              href="/sms-terms"
              className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
            >
              SMS Terms &amp; Consent
            </a>{" "}
            page.
          </p>
        </Section>

        <Section id="sharing" title="How We Share Information">
          <p>We may share information with:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Service providers who perform functions on our behalf (such as
              hosting, analytics, email delivery, and SMS delivery), under
              contractual restrictions limiting their use of the data
            </li>
            <li>
              Government authorities and other parties when required by law,
              subpoena, or legal process
            </li>
            <li>
              In connection with a merger, acquisition, or sale of business
              assets, subject to standard confidentiality protections
            </li>
          </ul>
        </Section>

        <Section id="retention" title="Data Retention">
          <p>
            We retain personal information for as long as needed to provide our
            services, comply with our legal obligations, resolve disputes, and
            enforce our agreements. Consent records for the SMS program are
            retained for the period required by applicable law and carrier
            audit standards.
          </p>
        </Section>

        <Section id="security" title="Security">
          <p>
            We implement reasonable administrative, technical, and physical
            safeguards designed to protect the personal information we hold.
            No method of transmission or storage is completely secure,
            however, and we cannot guarantee absolute security.
          </p>
        </Section>

        <Section id="your-choices" title="Your Choices">
          <p>You may:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Opt out of text messages at any time by replying STOP to any
              message
            </li>
            <li>
              Request access to, correction of, or deletion of personal
              information we hold about you, subject to applicable legal
              limitations
            </li>
            <li>
              Contact us using the information below to exercise any rights
              you have under applicable law
            </li>
          </ul>
        </Section>

        <Section id="children" title="Children's Privacy">
          <p>
            Our services are not directed to children under the age of 13, and
            we do not knowingly collect personal information from children
            under 13. If you believe we have collected such information,
            please contact us so we can promptly delete it.
          </p>
        </Section>

        <Section id="changes" title="Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. When we do,
            we will revise the "Last updated" date at the top of this page.
            Material changes will be communicated through reasonable means.
          </p>
        </Section>

        <Section id="contact" title="Contact Us">
          <p>For questions about this Privacy Policy, please contact us at:</p>
          <div className="mt-3 rounded-xl border border-slate-800 bg-slate-900/60 p-5 text-sm">
            <div className="text-slate-100 font-semibold">
              Cleveland Creative Group
            </div>
            <div className="text-slate-400">Cleveland, Ohio</div>
            <div className="text-slate-400 mt-1">
              Email:{" "}
              <a
                href="mailto:jamursch@gmail.com"
                className="text-cyan-300 hover:text-cyan-200"
              >
                jamursch@gmail.com
              </a>
            </div>
          </div>
        </Section>

        <p className="mt-12 text-xs text-slate-500 italic">
          This Privacy Policy is provided as a starting template for carrier
          review. It is not legal advice. Please have it reviewed by counsel
          familiar with your jurisdiction and business practices before
          relying on it.
        </p>
      </main>

      <footer className="border-t border-slate-900 py-10">
        <div className="max-w-4xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-xs text-slate-500 font-mono">
            © {new Date().getFullYear()} Cleveland Creative Group · Privacy
            Policy
          </div>
          <div className="flex gap-5 text-xs">
            <a href="/sms-terms" className="text-slate-400 hover:text-cyan-300">
              SMS Terms
            </a>
            <a href="/terms" className="text-slate-400 hover:text-cyan-300">
              Terms of Service
            </a>
            <a href="/" className="text-slate-400 hover:text-cyan-300">
              Back to site
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
